import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Banner from '../components/Banner';
import './LandingPage.css';

const LandingPage = () => {
  const featuresRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const aboutRef = useRef(null);
  const [aboutVisible, setAboutVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }

    return () => {
      if (featuresRef.current) {
        observer.unobserve(featuresRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAboutVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (aboutRef.current) {
      observer.observe(aboutRef.current);
    }

    return () => {
      if (aboutRef.current) {
        observer.unobserve(aboutRef.current);
      }
    };
  }, []);

  const features = [
    {
      id: 1,
      icon: '/images/feature-register.jpg',
      title: 'Register',
      description: 'Create your account as a parent and provide your details',
      emoji: '📝',
      color: '#2563eb'
    },
    {
      id: 2,
      icon: '/images/feature-browse-schools.jpg',
      title: 'Browse Schools',
      description: 'Search for your child\'s school and view available book kits',
      emoji: '🏫',
      color: '#10b981'
    },
    {
      id: 3,
      icon: '/images/feature-select-kits.jpg',
      title: 'Select Kits',
      description: 'Choose the book kit for your child\'s standard',
      emoji: '📚',
      color: '#f59e0b'
    },
    {
      id: 4,
      icon: '/images/feature-checkout.jpg',
      title: 'Checkout',
      description: 'Complete your purchase with secure payment',
      emoji: '💳',
      color: '#8b5cf6'
    }
  ];

  return (
    <div className="App">
      <Header />
      <div className="landing-page">
        <Banner />

        <div className="features-section" ref={featuresRef}>
          <div className="container">
            <h2 className={`section-title ${isVisible ? 'animate-title' : ''}`}>
              How It Works
            </h2>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div
                  key={feature.id}
                  className={`feature-card ${isVisible ? 'animate-in' : ''}`}
                  style={{
                    '--animation-delay': `${index * 0.15}s`,
                    '--feature-color': feature.color
                  }}
                >
                  <div className="feature-number">{feature.id}</div>
                  <div className="feature-icon-wrapper">
                    <div className="feature-icon">
                      <img src={feature.icon} alt={feature.title} />
                    </div>
                    <div className="feature-emoji">{feature.emoji}</div>
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <div className="feature-arrow">→</div>
                </div>
              ))}
            </div>
            <div className={`features-connector ${isVisible ? 'animate-connector' : ''}`}>
              <div className="connector-line"></div>
            </div>
          </div>
        </div>

        <div className="about-us-section" ref={aboutRef}>
          <div className="container">
            <h2 className={`section-title ${aboutVisible ? 'animate-title' : ''}`}>
              About SLN Book World
            </h2>
            
            <div className={`about-content ${aboutVisible ? 'fade-in-up' : ''}`}>
              <div className="about-main">
                <div className="about-text">
                  <h3>Empowering Schools with one stop solution</h3>
                  <p>
                    SLN Book World is a trusted name in educational resources, dedicated to providing 
                    comprehensive book kits and stationary supplies to students across India. With years 
                    of experience in the education sector, we understand the importance of quality learning 
                    materials in shaping young minds.
                  </p>
                  <p>
                    Our mission is to make quality education accessible to every student by offering 
                    school-approved book kits, complete stationary sets, and reliable delivery services. 
                    We partner with leading schools to ensure that students receive exactly what they 
                    need for their academic journey.
                  </p>
                </div>
                <div className="about-image-wrapper">
                  <img 
                    src="/images/about-main.jpg" 
                    alt="Books and Education" 
                    className="about-image"
                  />
                </div>
              </div>

              <div className="services-grid">
                <div className={`service-card ${aboutVisible ? 'fade-in-up' : ''}`} style={{ '--delay': '0.1s' }}>
                  <div className="service-icon">
                    <img src="/images/service-book-kits.jpg" alt="Book Kits" />
                  </div>
                  <h4>Complete Book Kits</h4>
                  <p>
                    We provide comprehensive book kits for all standards (1-10), carefully curated 
                    and approved by your school. Each kit contains all required textbooks and workbooks 
                    for the entire academic year.
                  </p>
                </div>

                <div className={`service-card ${aboutVisible ? 'fade-in-up' : ''}`} style={{ '--delay': '0.2s' }}>
                  <div className="service-icon">
                    <img src="/images/service-stationary.jpg" alt="Stationary" />
                  </div>
                  <h4>Stationary Supplies</h4>
                  <p>
                    From pens and pencils to notebooks and geometry boxes, we offer a complete range 
                    of high-quality stationary items. Our stationary kits are designed to meet all 
                    student requirements and are available in various packages.
                  </p>
                </div>

                <div className={`service-card ${aboutVisible ? 'fade-in-up' : ''}`} style={{ '--delay': '0.3s' }}>
                  <div className="service-icon">
                    <img src="/images/service-delivery.jpg" alt="Delivery" />
                  </div>
                  <h4>Flexible Delivery</h4>
                  <p>
                    Choose between convenient school pickup or home delivery options. We ensure timely 
                    delivery of all orders, so your child never misses a day of learning. Our delivery 
                    network covers major cities and towns across India.
                  </p>
                </div>

                <div className={`service-card ${aboutVisible ? 'fade-in-up' : ''}`} style={{ '--delay': '0.4s' }}>
                  <div className="service-icon">
                    <img src="/images/service-quality.jpg" alt="Quality" />
                  </div>
                  <h4>Quality Assurance</h4>
                  <p>
                    All our products undergo strict quality checks. We source books and stationary 
                    from reputed publishers and manufacturers, ensuring durability and educational 
                    value. Your satisfaction is our top priority.
                  </p>
                </div>
              </div>

              <div className="about-stationary">
                <div className="stationary-content">
                  <div className="stationary-image-wrapper">
                    <img 
                      src="/images/stationary-main.jpg" 
                      alt="Stationary Supplies" 
                      className="stationary-image"
                    />
                  </div>
                  <div className="stationary-text">
                    <h3>Complete Stationary Solutions</h3>
                    <p>
                      At SLN Book World, we understand that education goes beyond textbooks. That's why 
                      we offer comprehensive stationary packages that include everything your child needs 
                      for a successful academic year.
                    </p>
                    <ul className="stationary-list">
                      <li>✓ Writing Instruments (Pens, Pencils, Erasers, Sharpeners)</li>
                      <li>✓ Notebooks and Exercise Books (All sizes and formats)</li>
                      <li>✓ Geometry and Math Sets (Compass, Protractor, Rulers)</li>
                      <li>✓ Art Supplies (Crayons, Sketch Pens, Water Colors)</li>
                      <li>✓ School Bags and Accessories</li>
                      <li>✓ Lab Equipment and Science Kits</li>
                      <li>✓ Custom Stationary Packages as per School Requirements</li>
                    </ul>
                    <p>
                      Our stationary items are sourced from trusted manufacturers and are available 
                      in both individual items and complete kits. We ensure competitive pricing and 
                      bulk discounts for schools and institutions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LandingPage;

